/**
 * Textes d'interface en français — source unique pour l'UI.
 * Les valeurs métier/API (ex. status "active") sont mappées via les helpers exportés en bas de fichier.
 */
export const fr = Object.freeze({
  brand: "GymFlow",

  meta: {
    appDescription:
      "GymFlow — gestion de salle : abonnements, paiements et contrôle d'accès",
  },

  common: {
    loading: "Chargement…",
    save: "Enregistrer",
    cancel: "Annuler",
    close: "Fermer",
    export: "Exporter",
    previous: "Précédent",
    next: "Suivant",
    search: "Rechercher…",
    unknown: "Inconnu",
    view: "Voir",
    edit: "Modifier",
    delete: "Supprimer",
    viewAll: "Tout voir",
    addMember: "Ajouter un membre",
    noEmail: "Aucun e-mail",
    justNow: "À l'instant",
    saving: "Enregistrement…",
    updating: "Mise à jour…",
    recordPayment: "Enregistrer un paiement",
    searchAction: "Rechercher",
    reset: "Réinitialiser",
    id: "ID",
    stable: "Stable",
    google: "Google",
    github: "GitHub",
    actions: "Actions",
  },

  pagination: {
    members: (n, total) => `Affichage de ${n} sur ${total} membres`,
    payments: (n, total) => `Affichage de ${n} sur ${total} paiements`,
    subscriptions: (n, total) => `Affichage de ${n} sur ${total} abonnements`,
    logs: (n, total) => `Affichage de ${n} sur ${total} entrées`,
  },

  status: {
    active: "Actif",
    inactive: "Inactif",
    expired: "Expiré",
    pending: "En attente",
    granted: "Autorisé",
    denied: "Refusé",
    all: "Tous les statuts",
    allMethods: "Tous les modes",
  },

  paymentMethod: {
    cash: "Espèces",
    card: "Carte",
    transfer: "Virement",
  },

  gender: {
    label: "Genre",
    male: "Homme",
    female: "Femme",
  },

  nav: {
    dashboard: "Tableau de bord",
    members: "Membres",
    payments: "Paiements",
    products: "Produits",
    subscriptions: "Abonnements",
    accessTerminal: "Terminal d'accès",
    entranceDisplay: "Écran d’entrée",
    accessLogs: "Journal d'accès",
    teamEmployees: "Employés",
    teamCoaches: "Coachs",
    courses: "Cours",
    courseCalendar: "Calendrier des cours",
    expenses: "Dépenses",
    accounting: "Comptabilité",
    userAccounts: "Comptes utilisateurs",
    settings: "Paramètres",
    myGyms: "Mes salles",
    login: "Connexion",
    logout: "Déconnexion",
  },

  team: {
    employeesTitle: "Employés",
    employeesSubtitle: "Liste du personnel et rôles internes",
    coachesTitle: "Coachs",
    coachesSubtitle: "Liste des coachs et spécialisations",
    coursesTitle: "Cours",
    coursesSubtitle: "Catalogue des types de cours (nom, couleur, description).",
    coursesCalendarHint:
      "Ce n’est pas encore un planning : pour afficher un cours au calendrier, ouvrez « Calendrier des cours » et créez une séance (date, heure, coach) en choisissant ce type de cours.",
    add: "Ajouter",
    edit: "Modifier",
    name: "Nom",
    phone: "Téléphone",
    role: "Fonction",
    salary: "Salaire",
    specialization: "Spécialisation",
    userId: "ID utilisateur (optionnel)",
    linkedUser: "Compte lié",
    none: "—",
    description: "Description",
    color: "Couleur",
    createdAt: "Créé le",
    confirmDeleteEmployee: "Supprimer cet employé ?",
    confirmDeleteCoach: "Supprimer ce coach ?",
    confirmDeleteCourse: "Supprimer ce cours ?",
    noEmployees: "Aucun employé",
    noCoaches: "Aucun coach",
    noCourses: "Aucun cours",
    coachCalendarCol: "Calendrier",
  },

  calendarPage: {
    title: "Calendrier des cours",
    subtitle:
      "Chaque case correspond à une séance planifiée. Les types de cours se créent dans le menu « Cours » ; ici vous fixez date, heure et coach.",
    coachView: "Calendrier coach",
    allCoaches: "Tous les coachs",
    weekPrev: "Semaine précédente",
    weekNext: "Semaine suivante",
    weekToolbar: "Navigation semaine",
    endAfterStart: "L’heure de fin doit être après l’heure de début.",
    filterCoach: "Filtrer par coach",
    room: "Salle",
    participants: "Places max",
    addSession: "Nouvelle séance",
    course: "Cours",
    coach: "Coach",
    starts: "Début",
    ends: "Fin",
    sessionDateLabel: "Date",
    startTimeLabel: "Heure de début",
    endTimeLabel: "Heure de fin",
    monthPrev: "Mois précédent",
    monthNext: "Mois suivant",
    monthHint:
      "Cliquez sur un jour : la semaine s’affiche et la nouvelle séance est préremplie (ajustez l’heure ci-dessous).",
    weekdaysShort: ["Lu", "Ma", "Me", "Je", "Ve", "Sa", "Di"],
    scheduleThisDay: "Planifier ce jour",
    scheduleModeSingle: "Une date",
    scheduleModeMulti: "Plusieurs jours",
    multiMonthHint: "Cliquez les jours : chaque clic ajoute ou retire une date (même horaire pour toutes les séances).",
    multiDaysSelected: "{{count}} jour(s) sélectionné(s)",
    clearMultiDays: "Effacer",
    multiDaysRequired: "Sélectionnez au moins un jour dans le calendrier.",
    bulkCreated: "{{n}} séance(s) créée(s).",
    bulkPartialError: "{{n}} erreur(s) — vérifiez les créneaux ou doublons.",
    bulkSubmitLabel: "Créer {{n}} séance(s)",
    confirmDeleteSession: "Supprimer cette séance ?",
    emptyWeek: "Aucune séance cette semaine.",
    emptyWeekHint:
      "Un « cours » dans le menu Cours est seulement un modèle : ajoutez une séance ici (bouton « Nouvelle séance ») en choisissant le cours, le coach et la date dans la semaine affichée.",
    openCoachCalendar: "Voir le calendrier de ce coach",
  },

  gyms: {
    title: "Mes salles",
    subtitle: "Gérez vos salles et basculez entre elles",
    listTitle: "Salles accessibles",
    listDescription: "Chaque salle possède ses propres membres, abonnements et paramètres.",
    empty: "Aucune salle accessible",
    activeGym: "Salle active",
    switchTo: "Basculer",
    switcherLabel: "Changer de salle",
    createTitle: "Créer une nouvelle salle",
    createDescription: "Vous serez propriétaire de la nouvelle salle et basculerez automatiquement dessus.",
    createButton: "Créer la salle",
    createError: "Impossible de créer la salle",
    formName: "Nom de la salle",
    formCity: "Ville",
    formPhone: "Téléphone",
    formEmail: "E-mail de contact",
    formAddress: "Adresse",
    rolesLabel: "Vos rôles",
    manageLink: "Gérer mes salles",
  },

  userAccounts: {
    title: "Comptes utilisateurs",
    subtitle: "Comptes et rôles pour la salle active uniquement. Un e-mail existant sera rattaché sans changer son mot de passe.",
    email: "E-mail",
    password: "Mot de passe",
    passwordHint: "Requis uniquement pour un nouveau compte",
    name: "Nom affiché",
    roles: "Rôles",
    create: "Créer ou rattacher",
    tableEmail: "E-mail",
    tableName: "Nom",
    tableRoles: "Rôles",
    empty: "Aucun compte",
    roleOwner: "Propriétaire",
    roleAdmin: "Admin",
    roleEmployee: "Employé",
    roleCoach: "Coach",
  },

  navbar: {
    toggleMenu: "Ouvrir ou fermer le menu",
    closeMenu: "Fermer le menu",
    collapseSidebar: "Réduire le menu latéral",
    notifications: "Notifications",
    searchPlaceholder: "Rechercher…",
    adminUser: "Administrateur",
  },

  dashboard: {
    title: "Tableau de bord",
    subtitle: "Bienvenue sur GymFlow",
    chartLoading: "Chargement…",
    stats: {
      totalMembers: "Membres au total",
      monthlyRevenue: "Chiffre d'affaires (mois)",
      activeSubscriptions: "Abonnements actifs",
      entriesToday: "Entrées aujourd'hui",
      changeFromLastMonth: "vs mois dernier",
      changeFromLastWeek: "vs semaine dernière",
      newThisWeek: "nouveaux cette semaine",
      moreThanYesterday: "de plus qu'hier",
      percentOfTotal: "% du total",
      needsRenewal: "À renouveler",
      allTimeRevenue: "Cumul historique",
    },
    charts: {
      revenueLabel: "Chiffre d'affaires",
      membersLabel: "Membres",
      revenueTitle: "Vue d'ensemble du CA",
      revenueHint: "Chiffre d'affaires mensuel sur l'année",
      growthTitle: "Croissance des membres",
      growthHint: "Nouvelles inscriptions dans le temps",
      noRevenue: "Aucune donnée de chiffre d'affaires",
      noMembers: "Aucune donnée membres",
    },
    recentMembers: "Membres récents",
    recentActivity: "Activité récente",
    emptyMembersTitle: "Aucun membre pour l'instant",
    emptyMembersDesc: "Ajoutez des membres pour les voir ici",
    emptyActivityTitle: "Aucune activité",
    emptyActivityDesc: "Les entrées apparaîtront ici",
  },

  members: {
    title: "Membres",
    subtitle: "Gérez les membres de votre salle",
    searchPlaceholder: "Rechercher par nom ou e-mail…",
    table: {
      member: "Membre",
      email: "E-mail",
      subscription: "Abonnement",
      status: "Statut",
      joinDate: "Date d'inscription",
    },
    emptyTitle: "Aucun membre trouvé",
    emptyFiltered: "Modifiez la recherche ou le filtre",
    emptyInitial: "Ajoutez votre premier membre",
    confirmDelete: "Supprimer ce membre ?",
    filtersTitle: "Recherche de membres",
    placeholders: { name: "Nom", email: "E-mail", phone: "Téléphone", cin: "CIN" },
    tableLegacy: {
      photo: "Photo",
      name: "Nom",
      phone: "Téléphone",
      actions: "Actions",
    },
  },

  payments: {
    title: "Paiements",
    subtitle: "Suivez et gérez tous les paiements",
    searchPlaceholder: "Rechercher par membre ou n° de paiement…",
    stats: {
      totalRevenue: "Chiffre d'affaires total",
      monthlyRevenue: "CA du mois",
      totalPayments: "Nombre de paiements",
      averagePayment: "Paiement moyen",
    },
    table: {
      id: "N° paiement",
      member: "Membre",
      amount: "Montant",
      method: "Mode",
      date: "Date",
    },
    emptyTitle: "Aucun paiement",
    emptyFiltered: "Modifiez la recherche ou le filtre",
    emptyInitial: "Les paiements apparaîtront ici",
    detail: {
      title: "Paiement",
      subtitle: "Détail, facture PDF et mentions",
      back: "Retour aux paiements",
      notFound: "Ce paiement est introuvable.",
      loadError: "Impossible de charger ce paiement.",
      invoiceSection: "Facture",
      memberSection: "Membre",
      memberLink: "Ouvrir la fiche membre",
      subscriptionSection: "Abonnement lié",
      noSubscription: "Aucun abonnement associé à ce paiement.",
      notesLabel: "Notes facture",
    },
  },

  expensesPage: {
    title: "Dépenses",
    subtitle: "Suivi des charges par type pour votre salle",
    typesTitle: "Types de dépense",
    typesSubtitle: "Personnalisez les catégories (par salle)",
    listTitle: "Historique des dépenses",
    filterType: "Filtrer par type",
    allTypes: "Tous les types",
    addType: "Nouveau type",
    addExpense: "Nouvelle dépense",
    typeName: "Libellé du type",
    typeCode: "Code interne (optionnel)",
    typeColor: "Couleur",
    sortOrder: "Ordre d’affichage",
    expenseTitle: "Libellé",
    amount: "Montant",
    date: "Date",
    type: "Type",
    totalPeriod: "Total affiché",
    confirmDeleteType: "Supprimer ce type ? Les dépenses qui l’utilisent doivent être modifiées avant.",
    confirmDeleteExpense: "Supprimer cette dépense ?",
    emptyTypes: "Aucun type défini",
    emptyExpenses: "Aucune dépense enregistrée",
    noColor: "—",
  },

  accountingPage: {
    title: "Comptabilité",
    subtitle: "Synthèse des encaissements et des charges sur la période choisie",
    periodFrom: "Du",
    periodTo: "au",
    apply: "Actualiser",
    revenue: "Encaissements",
    expenses: "Dépenses",
    net: "Résultat",
    paymentCount: "Paiements",
    expenseCount: "Écritures de charges",
    byType: "Charges par type",
    monthlyChart: "Encaissements et dépenses par mois",
    typeName: "Type",
    amount: "Montant",
    share: "Part",
    emptyTypes: "Aucune dépense sur cette période",
    hint: "Les encaissements proviennent des paiements enregistrés ; les dépenses de l’onglet Dépenses.",
    printOrPdf: "Imprimer / enregistrer en PDF",
    printAria: "Ouvre la boîte d’impression : choisissez une imprimante ou « Enregistrer au format PDF ».",
    periodLabel: "Période",
    productSalesInPeriod: (n) =>
      n === 1 ? "1 vente boutique" : `${n} ventes boutique`,
  },

  productsPage: {
    title: "Produits & boutique",
    subtitle:
      "Catalogue par salle, codes-barres (scanner POS), stock, encaissement et factures PDF (PF-…).",
    tabCatalog: "Catalogue",
    tabSale: "Vente",
    tabHistory: "Historique",
    newProduct: "Nouveau produit",
    editProduct: "Modifier le produit",
    catalogList: "Produits",
    name: "Nom",
    description: "Description",
    unitPrice: "Prix de vente (MAD)",
    purchasePrice: "Prix d’achat (MAD)",
    purchasePriceHint: "Pour le calcul de marge (prix de vente − prix d’achat).",
    margin: "Marge",
    marginPercent: "Marge %",
    colPurchase: "P. achat",
    colMargin: "Marge",
    subtotal: "Sous-total",
    discountMad: "Remise (MAD)",
    discountPercent: "Remise (%)",
    discountHint: "La remise s’applique au panier avant encaissement. La facture PDF reprend sous-total, remise et net.",
    discountApproxEquiv: (pct) => `≈ ${pct} % du sous-total`,
    amountDue: "Net à payer",
    barcode: "Code-barres (EAN / interne)",
    barcodePlaceholder: "Scanner ou saisir le code…",
    barcodeHint:
      "Optionnel, unique par salle. Même zone pour le lecteur ou la saisie ; Entrée après scan vérifie les doublons.",
    barcodeAlreadyUsed: (name) =>
      `Ce code-barres est déjà utilisé par « ${name} ». Changez-le ou modifiez l’autre produit.`,
    expiryDate: "Date d’expiration (DLC)",
    expiryHint:
      "Optionnel. Une alerte s’affiche dans la boutique si la date est dans les 30 prochains jours ou dépassée.",
    shopAlertsHeading: "Alertes boutique",
    expiryAlertTitle: "Dates d’expiration",
    expiryAlertSubtitle: "Produits actifs dont la DLC est dépassée ou dans les 30 prochains jours.",
    stockAlertTitle: "Stock",
    stockAlertSubtitle:
      "Produits actifs avec stock suivi : rupture ou stock faible (seuil affiché sous la liste).",
    stockLowThresholdNote: (n) => `Seuil « stock faible » : ${n} unité(s) ou moins.`,
    stockOut: "Rupture",
    stockLow: "Stock faible",
    stockUnitsLeft: (n) => `${n} en stock`,
    productModalHint: "Les champs obligatoires sont indiqués. Code-barres et date d’expiration sont optionnels.",
    addProduct: "Nouveau produit",
    expiryExpired: "Expiré",
    expirySoon: "Bientôt",
    expiryDaysAgo: (n) => `Expiré depuis ${n} jour(s)`,
    expiryDaysLeft: (n) => `Dans ${n} jour(s)`,
    colExpiry: "Expiration",
    trackStock: "Gérer le stock",
    stockQuantity: "Quantité en stock",
    stockUnlimited: "Illimité",
    stockLabel: "Stock",
    colBarcode: "Code-barres",
    colStock: "Stock",
    scanTitle: "Scanner le code-barres",
    saleAddSection: "Ajouter au panier",
    saleSearchLabel: "Rechercher un produit",
    saleSearchPlaceholder: "Nom ou code-barres…",
    saleSearchEmpty: "Aucun produit ne correspond.",
    saleSearchHint: "Cliquez sur une ligne ou validez avec Entrée si un seul résultat.",
    scanPlaceholder: "Scannez puis Entrée…",
    scanSubmit: "Ajouter",
    scanNotFound: "Aucun produit actif avec ce code.",
    scanOutOfStock: "Rupture de stock pour ce produit.",
    stockInsufficient: "Quantité supérieure au stock disponible.",
    active: "Visible à la vente",
    status: "État",
    statusActive: "Actif",
    statusInactive: "Retiré",
    create: "Créer",
    save: "Enregistrer",
    cancel: "Annuler",
    actions: "Actions",
    loading: "Chargement…",
    emptyCatalog: "Aucun produit pour l’instant. Utilisez « Nouveau produit » pour en ajouter.",
    confirmDelete: "Supprimer ce produit ? Les lignes d’anciennes ventes conservent le libellé.",
    newSale: "Nouvelle vente",
    member: "Membre (optionnel)",
    memberNone: "— Client sans fiche",
    buyerName: "Nom du client",
    buyerPlaceholder: "Si pas de membre sélectionné",
    lines: "Lignes",
    pickProduct: "Choisir un produit",
    addLine: "Ajouter une ligne",
    method: "Mode de paiement",
    total: "Total",
    invoiceNotes: "Notes sur la facture",
    invoiceNotesPlaceholder: "Optionnel",
    recordSale: "Encaisser et télécharger la facture",
    cartEmpty: "Ajoutez au moins un produit.",
    buyerRequired: "Sélectionnez un membre ou saisissez le nom de l’acheteur (2 caractères minimum).",
    salesHistory: "Ventes enregistrées",
    refresh: "Actualiser",
    emptySales: "Aucune vente pour l’instant.",
    colDate: "Date",
    colInvoice: "Facture",
    colClient: "Client",
    colTotal: "Montant",
    colMethod: "Mode",
  },

  subscriptions: {
    title: "Abonnements",
    subtitle: "Gérez les abonnements des membres",
    new: "Nouvel abonnement",
    searchPlaceholder: "Rechercher par membre ou pack…",
    stats: {
      total: "Abonnements au total",
      active: "Actifs",
      expired: "Expirés",
      totalValue: "Valeur totale",
      newThisMonth: "nouveaux ce mois-ci",
    },
    table: {
      member: "Membre",
      pack: "Pack",
      plan: "Formule",
      startDate: "Début",
      endDate: "Fin",
      status: "Statut",
      price: "Prix",
    },
    emptyTitle: "Aucun abonnement",
    emptyFiltered: "Modifiez la recherche ou le filtre",
    emptyInitial: "Créez un premier abonnement",
    detail: {
      title: "Abonnement",
      subtitle: "Tarif, solde et paiements liés",
      back: "Retour aux abonnements",
      notFound: "Cet abonnement est introuvable.",
      loadError: "Impossible de charger cet abonnement.",
      pricingSection: "Tarif et solde",
      paymentsSection: "Paiements liés",
      downloadInvoice: "Télécharger la facture PDF",
      invoiceUnavailable: "Aucun paiement enregistré : facture indisponible.",
      invoiceHint:
        "Un seul PDF récapitule tous les encaissements liés à cet abonnement (lignes + totaux).",
      openPayment: "Voir le paiement",
    },
  },

  accessLogs: {
    title: "Journal d'accès",
    subtitle: "Consultez les entrées à la salle",
    searchPlaceholder: "Rechercher par nom de membre…",
    table: {
      member: "Membre",
      method: "Méthode",
      status: "Statut",
      dateTime: "Date et heure",
    },
    emptyTitle: "Aucune entrée",
    emptyFiltered: "Modifiez la recherche ou le filtre",
    emptyInitial: "Les passages s'afficheront quand les membres entreront",
  },

  entranceDisplay: {
    backToApp: "← Tableau de bord",
    defaultGymName: "LA SALLE",
    idleHint: "En attente d’un passage…",
    grantedLine: "Accès autorisé",
    grantedVenue: (name) => (name ? `Bienvenue à ${name}` : ""),
    deniedLine: "Accès refusé",
    fallbackName: "Membre",
    photoPlaceholder: "Pas de photo sur la fiche",
    detail: {
      granted: "Abonnement valide.",
      denied: "Accès non autorisé.",
      expired: "Abonnement expiré.",
    },
    /** Codes renvoyés par l’API (access_log.reason) */
    reasonByKey: {
      access_blocked: "Accès désactivé pour ce membre (paramétrage salle).",
      no_subscription: "Aucun abonnement actif.",
      subscription_expired: "La date de fin d’abonnement est dépassée.",
    },
  },

  accessTerminal: {
    title: "Terminal d'accès",
    subtitle:
      "Vérification par visage : clignez des yeux lorsque c'est demandé pour confirmer votre présence.",
    camera: "Caméra",
    cameraHint: "Choisissez un périphérique et placez-vous dans le cadre.",
    cameraFallback: (id) => `Caméra ${id.slice(0, 8)}…`,
    welcome: (name) => `Bienvenue, ${name}`,
    status: {
      waiting: "En attente d'un membre",
      blink: "Clignez des yeux pour vérifier",
      checking: "Vérification…",
      granted: "Accès autorisé",
      denied: "Accès refusé",
      error: "Erreur serveur",
    },
  },

  settings: {
    title: "Paramètres",
    subtitle: "Configurez votre salle et votre compte",
    tabs: {
      gym: "Profil de la salle",
      account: "Compte",
      notifications: "Notifications",
      security: "Sécurité",
      appearance: "Apparence",
    },
    gym: {
      title: "Profil de la salle",
      description: "Coordonnées et informations de la salle active",
      gymName: "Nom de la salle",
      email: "E-mail",
      phone: "Téléphone",
      city: "Ville",
      address: "Adresse",
      saveChanges: "Enregistrer les modifications",
      saveError: "Impossible d’enregistrer le profil",
      manageGymsLink: "Gérer mes salles",
      displayScreenTitle: "Écran d’entrée (TV)",
      displayScreenDescription:
        "Un lien dédié à cette salle : ouvrez-le en plein écran sur la TV ; les passages s’affichent sans session (jeton ci-dessous).",
      displayUrlLabel: "Lien pour la télé",
      displayCopy: "Copier le lien",
      displayRegenerate: "Régénérer le jeton",
      displayRegenerateConfirm:
        "L’ancien lien ne fonctionnera plus sur la TV. Les appareils devront utiliser le nouveau lien. Continuer ?",
      displayTokenHint: "Confidentiel : ne le partagez pas hors de votre salle.",
      displayLoading: "Chargement du jeton…",
      agentTitle: "Mini-agent contrôle d’accès (LAN)",
      agentDescription:
        "Copiez ce jeton dans le fichier .env du mini-PC branché au terminal Hikvision (GYMFLOW_AGENT_TOKEN). Il permet la sync des membres et le kill-switch à distance.",
      agentApiUrl: "URL API Gymflow",
      agentTokenLabel: "Jeton agent (GYMFLOW_AGENT_TOKEN)",
      agentCopy: "Copier",
      agentRegenerate: "Régénérer le jeton",
      agentRegenerateConfirm:
        "L’ancien jeton cessera de fonctionner sur le mini-PC. Il faudra mettre à jour le .env. Continuer ?",
      agentLoading: "Chargement du jeton agent…",
      agentLicenseTitle: "Licence agent",
      agentStatus: "Statut",
      agentLastSeen: "Dernière vue",
      agentLastSeenNever: "Jamais (agent pas encore connecté)",
      agentBoundPc: "PC lié (empreinte)",
      agentBoundNone: "—",
    },
    account: {
      title: "Compte utilisateur",
      description: "Informations personnelles du compte",
      fullName: "Nom complet",
      email: "E-mail",
    },
    notifications: {
      title: "Préférences de notification",
      description: "Choisissez comment vous être informé",
      items: [
        {
          label: "Notifications par e-mail",
          description: "Recevoir des mises à jour sur votre salle",
        },
        {
          label: "Alertes nouveaux membres",
          description: "Être averti des nouvelles inscriptions",
        },
        {
          label: "Notifications de paiement",
          description: "Alertes lors de nouveaux paiements",
        },
        {
          label: "Fin d'abonnement",
          description: "Rappels avant expiration des abonnements",
        },
      ],
    },
    security: {
      title: "Sécurité",
      description: "Mot de passe et options de sécurité",
      currentPassword: "Mot de passe actuel",
      newPassword: "Nouveau mot de passe",
      confirmPassword: "Confirmer le mot de passe",
      currentPlaceholder: "Saisissez le mot de passe actuel",
      newPlaceholder: "Saisissez le nouveau mot de passe",
      confirmPlaceholder: "Confirmez le nouveau mot de passe",
      updatePassword: "Mettre à jour le mot de passe",
    },
    appearance: {
      title: "Apparence",
      description: "Personnalisez l'interface du tableau de bord",
      theme: "Thème",
      dark: "Sombre",
      light: "Clair",
    },
    packs: {
      title: "Packs et formules",
      subtitle:
        "Configurez les packs (type d’offre, prix de base) et les formules (durée, prix de la période) utilisés dans « Nouvel abonnement ».",
      backToSettings: "Retour aux paramètres",
      addPack: "Nouveau pack",
      manageLink: "Gérer les packs et formules",
      manageDescription:
        "Gérez les packs et les formules (durées / tarifs) proposés aux membres lors de la souscription.",
      sectionPacksTitle: "Packs",
      sectionPacksDescription: "Types d’offre ou d’activité et tarif de base (s’ajoute au prix de la formule).",
      sectionPlansTitle: "Formules",
      sectionPlansDescription:
        "Modèles de durée (nom, mois). Le prix de la formule pour chaque pack se règle dans la grille tarifaire ci-dessous.",
      matrixTitle: "Grille tarifaire (pack × formule)",
      matrixHint:
        "Pour chaque croisement pack / durée, indiquez le montant « formule » (s’ajoute au prix de base du pack pour le total).",
      matrixPackColumn: "Pack / formule",
      matrixSave: "Enregistrer la grille",
      matrixSaveError: "Impossible d’enregistrer la grille tarifaire.",
      matrixNeedBoth: "Créez au moins un pack et une formule pour éditer les tarifs.",
      addPlan: "Nouvelle formule",
      tableName: "Nom",
      tablePrice: "Prix (base)",
      tableDescription: "Description",
      planTableDuration: "Durée",
      planMonthsSuffix: "mois",
      emptyTitle: "Aucun pack",
      emptyDescription: "Ajoutez un premier pack pour l’utiliser dans le flux « Nouvel abonnement ».",
      planEmptyTitle: "Aucune formule",
      planEmptyDescription: "Ajoutez une formule (ex. 1 mois, 3 mois) pour compléter les offres.",
      modalCreateTitle: "Nouveau pack",
      modalEditTitle: "Modifier le pack",
      modalHint: "Le prix s’ajoute à celui de la formule pour calculer le total de l’abonnement.",
      fieldName: "Nom du pack",
      fieldPrice: "Prix de base (MAD)",
      fieldDescription: "Description (optionnel)",
      descriptionPlaceholder: "Ex. Accès salles musculation et machines guidées",
      confirmDelete: "Supprimer ce pack ? Cette action est irréversible.",
      planModalCreateTitle: "Nouvelle formule",
      planModalEditTitle: "Modifier la formule",
      planModalHint:
        "Définissez le libellé et la durée. Les montants par pack se saisissent dans la grille « pack × formule ».",
      planFieldName: "Nom de la formule",
      planFieldDuration: "Durée (mois)",
      planFieldDescription: "Description (optionnel)",
      planDescriptionPlaceholder: "Ex. Engagement mensuel renouvelable",
      planConfirmDelete: "Supprimer cette formule ? Cette action est irréversible.",
      loadError: "Impossible de charger les packs et formules.",
      saveError: "Enregistrement impossible.",
      deleteError: "Suppression impossible.",
      planSaveError: "Enregistrement de la formule impossible.",
      planDeleteError: "Suppression de la formule impossible.",
      nameRequired: "Indiquez un nom de pack.",
      priceInvalid: "Indiquez un prix valide (≥ 0).",
      planNameRequired: "Indiquez un nom de formule.",
      planDurationInvalid: "Indiquez une durée en mois (nombre entier ≥ 1).",
    },
  },

  auth: {
    login: {
      heroAlt: "Personne s’entraînant avec des haltères dans une salle de sport moderne",
      heroTagline: "Gérez votre salle. Boostez vos performances.",
      title: "Bon retour",
      subtitle: "Connectez-vous pour continuer",
      email: "E-mail",
      password: "Mot de passe",
      passwordPlaceholder: "Saisissez votre mot de passe",
      forgotPassword: "Mot de passe oublié ?",
      submit: "Se connecter",
      submitting: "Connexion…",
      orContinue: "Ou continuer avec",
      noAccount: "Pas encore de compte ?",
      signUp: "Créer un compte",
      footer: "En vous connectant, vous acceptez nos",
      terms: "Conditions d'utilisation",
      and: "et",
      privacy: "Politique de confidentialité",
      errorInvalid: "E-mail ou mot de passe incorrect",
    },
    signup: {
      title: "Créer un compte",
      subtitle: "Commencez avec GymFlow",
      fullName: "Nom complet",
      namePlaceholder: "Jean Dupont",
      emailPlaceholder: "vous@exemple.com",
      password: "Mot de passe",
      passwordCreatePlaceholder: "Créez un mot de passe",
      confirmPassword: "Confirmer le mot de passe",
      confirmPlaceholder: "Confirmez votre mot de passe",
      submit: "Créer le compte",
      submitting: "Création du compte…",
      orContinue: "Ou continuer avec",
      hasAccount: "Déjà un compte ?",
      signIn: "Se connecter",
      footer: "En créant un compte, vous acceptez nos",
      errorMismatch: "Les mots de passe ne correspondent pas",
      errorMinLength: "Le mot de passe doit contenir au moins 8 caractères",
      errorCreate: "Impossible de créer le compte",
      rules: {
        minLength: "Au moins 8 caractères",
        hasNumber: "Contient un chiffre",
        hasUppercase: "Contient une majuscule",
      },
    },
  },

  wizard: {
    title: "Nouvel abonnement",
    subtitle:
      "Création du membre, choix de l’abonnement, paiement et confirmation — tout en un flux guidé.",
    steps: ["Membre", "Abonnement", "Paiement", "Confirmation"],
    stepMember: {
      formTitle: "Informations membre",
      captureTitle: "Photo du membre",
      captureHint:
        "PC sans webcam : importez une photo (téléphone / galerie). Avec webcam : capture automatique.",
      firstName: "Prénom *",
      lastName: "Nom *",
      phone: "Téléphone *",
      emailPlaceholder: "E-mail",
      cin: "CIN",
      profession: "Profession",
      address: "Adresse",
      notes: "Notes",
      createMember: "Créer le membre",
      faceMessages: {
        multiple: "Une seule personne à la fois",
        none: "Aucun visage détecté",
        center: "Centrez votre visage",
        light: "Améliorez l'éclairage",
        hold: "Ne bougez pas…",
        done: "Visage capturé ✓",
      },
      uploadPhoto: "Importer une photo",
      uploadHint: "JPG, PNG ou photo reçue sur le téléphone",
      uploadProcessing: "Traitement de la photo…",
      uploadOkNoFace:
        "Photo enregistrée. Visage non détecté : la sync terminal utilisera quand même l’image.",
      uploadOkFace: "Photo et visage prêts ✓",
      cameraUnavailable:
        "Pas de caméra sur ce PC — utilisez « Importer une photo » (galerie / WhatsApp).",
      photoOptional:
        "Photo facultative à l’inscription : vous pourrez l’ajouter plus tard sur la fiche membre.",
      retake: "Reprendre la photo",
      faceAlt: "Aperçu du visage capturé",
    },
    stepSubscription: {
      title: "Choisir l'abonnement",
      helper:
        "Choisissez d’abord un pack, puis une formule : le tarif formule dépend du pack (comme défini dans les paramètres). Le total = prix du pack + prix formule pour ce couple.",
      pickPackFirst: "Choisissez d’abord un pack pour afficher les formules et leurs tarifs.",
      loadingPlansForPack: "Chargement des formules…",
      packTitle: "Choisir un pack",
      planTitle: "Choisir une formule",
      pricingTableTitle: "Tarifs pour ce pack",
      pricingTableHint:
        "Montants pour le pack sélectionné : prix formule (selon la grille) + prix de base du pack. Cliquez sur une ligne pour choisir la formule.",
      colPlanName: "Formule",
      colDuration: "Durée",
      colFormulaPrice: "Prix formule",
      colTotal: "Total",
      tileTotalLabel: "Total",
      tileFormulaDetail: "dont formule",
      planCardsHint: "Vue cartes (même sélection que le tableau)",
      startDate: "Date de début",
      summaryTitle: "Récapitulatif",
      packLine: "Pack",
      planLine: "Formule",
      packBaseLine: "Prix pack (base)",
      formulaLine: "Prix formule (pour ce pack)",
      startLine: "Début",
      endLine: "Fin",
      priceLine: "Total",
      next: "Suivant →",
      perMonth: "MAD (base)",
      loadingOptions: "Chargement des offres…",
      noPacks: "Aucun pack configuré. Ajoutez des packs dans Paramètres.",
      noPlans:
        "Aucune formule (durée) n’est disponible pour votre salle. Ajoutez des formules dans Paramètres → Packs et formules.",
      loadOptionsError: "Impossible de charger packs et formules.",
    },
    plans: [
      { id: 1, name: "1 mois", months: 1 },
      { id: 2, name: "3 mois", months: 3 },
      { id: 3, name: "6 mois", months: 6 },
      { id: 4, name: "12 mois", months: 12 },
    ],
    stepPayment: {
      title: "Paiement",
      memberLabel: "Membre",
      amount: "Montant",
      method: "Mode de paiement",
      confirm: "Confirmer le paiement",
      subscriptionTotal: "Total de l’abonnement",
      paymentTypeLabel: "Type de paiement",
      paymentTypeFull: "Paiement complet",
      paymentTypePartial: "Paiement partiel (acompte)",
      paymentTypeHint:
        "Si le montant payé est inférieur au total, indiquez « partiel ». Vous pouvez aussi le laisser en automatique.",
      invoiceNotes: "Notes sur la facture (optionnel)",
      invoiceNotesPlaceholder: "Mentions affichées sur le PDF…",
    },
    stepSuccess: {
      title: "Abonnement créé ✓",
      memberNumber: "Numéro de membre :",
      goMembers: "Voir les membres",
      downloadInvoice: "Télécharger la facture PDF",
      editInvoice: "Modifier la facture",
      accessQrLabel: "QR d’accès (terminal)",
      selfieSection: "Photo manquante ? Selfie téléphone",
    },
  },

  invoice: {
    editTitle: "Facture",
    editHint: "Ces informations apparaissent sur le PDF (type complet / partiel et notes).",
    paymentTypeLabel: "Type de paiement",
    paymentTypeFull: "Paiement complet",
    paymentTypePartial: "Paiement partiel / acompte",
    invoiceNotes: "Notes sur la facture",
    notesPlaceholder: "Mentions, conditions particulières…",
    downloadPdf: "PDF",
    editInvoice: "Facture",
    save: "Enregistrer",
    loadError: "Impossible de charger le paiement.",
    saveError: "Impossible d’enregistrer.",
    downloadError: "Impossible de télécharger la facture.",
  },

  selfie: {
    deskTitle: "Selfie téléphone",
    deskHint:
      "Affichez le QR : le membre scanne, prend la photo sur son téléphone, elle arrive ici automatiquement.",
    showQr: "Afficher le QR selfie",
    refresh: "Nouveau QR",
    generating: "Génération…",
    linkError: "Impossible de générer le lien selfie.",
    waitingPhone: "En attente du selfie sur le téléphone…",
    scanHint: "Le membre scanne ce QR avec l’appareil photo du téléphone.",
    expiresAt: "Valide jusqu’à {{time}}",
    received: "Photo reçue ✓ — sync terminal au prochain cycle.",
    pageTitle: "Votre photo Gymflow",
    pageHint: "Bonjour {{name}}, centrez votre visage puis validez.",
    memberFallback: "membre",
    capture: "Prendre la photo",
    pickGallery: "Choisir dans la galerie",
    send: "Envoyer",
    sending: "Envoi…",
    retake: "Reprendre",
    cameraFail: "Caméra inaccessible — utilisez la galerie.",
    thanksTitle: "Merci !",
    thanksBody: "{{name}}, votre photo est enregistrée. Vous pouvez rendre le téléphone.",
    loading: "Chargement…",
    invalidLink: "Lien invalide. Demandez un nouveau QR à l’accueil.",
    expiredLink: "Lien expiré. Demandez un nouveau QR à l’accueil.",
    uploadError: "Envoi impossible. Réessayez ou demandez un nouveau QR.",
  },

  memberDetail: {
    profileTitle: "Fiche membre",
    backToList: "Retour aux membres",
    editProfile: "Modifier",
    memberNumberLabel: "N° membre",
    subscriptionUntil: "Fin d'abonnement",
    editTitle: "Modifier le membre",
    infoTitle: "Informations membre",
    infoDescription: "Identité et coordonnées du membre",
    phone: "Téléphone",
    email: "E-mail",
    cin: "CIN",
    gender: "Genre",
    birthDate: "Date de naissance",
    profession: "Profession",
    address: "Adresse",
    photoUpload: "Importer une photo",
    photoChange: "Changer la photo",
    photoUploading: "Enregistrement…",
    photoUploadOk: "Photo mise à jour. Sync terminal au prochain cycle.",
    photoUploadOkNoFace:
      "Photo enregistrée (visage non détecté). Sync terminal au prochain cycle.",
    photoUploadError: "Impossible d’enregistrer la photo.",
    subscriptions: "Abonnements",
    payments: "Paiements",
    paymentsHint: "Téléchargez la facture PDF pour chaque encaissement.",
    downloadInvoice: "Télécharger la facture",
    invoiceNumberCol: "N° facture",
    accessHistory: "Historique d'accès",
    noSubscriptions: "Aucun abonnement",
    paymentBalanceTitle: "Encaissements et solde",
    paymentBalanceHint:
      "Total de tous les encaissements et reste à payer sur les montants d’abonnement (prix − paiements liés).",
    totalPaidAll: "Total payé",
    totalRemainingAll: "Reste à payer (abonnements)",
    subscriptionPaidCol: "Payé",
    subscriptionRemainingCol: "Reste",
    accessGymTitle: "Accès à la salle",
    accessBlockedLabel: "Bloquer l’entrée (QR / visage / terminal)",
    accessBlockedHint:
      "Si activé : refus côté Gymflow et désactivation sur le terminal au prochain sync agent (quelques minutes).",
    accessBlockedBanner: "L’accès à la salle est désactivé pour ce membre.",
    qrLabel: "QR membre (terminal)",
    qrHint: "À présenter face à la caméra du terminal Hikvision (contenu = n° membre).",
    subscriptionAlertExpired: "Abonnement expiré (fin le {{date}}).",
    subscriptionAlert1d:
      "Échéance imminente : fin d’abonnement le {{date}} (aujourd’hui ou demain).",
    subscriptionAlert7d: "L’abonnement se termine dans moins de 7 jours (fin le {{date}}).",
    subscriptionAlert30d: "L’abonnement se termine dans moins d’un mois (fin le {{date}}).",
    accessUpdateError: "Impossible de mettre à jour l’accès.",
    noPayments: "Aucun paiement",
    noAccessLogs: "Aucun passage enregistré",
    newSubscription: "Nouvel abonnement",
    addPayment: "Ajouter un paiement",
    amountPlaceholder: "Montant",
    viewProfile: "Voir la fiche",
    paymentCardTitle: "Encaissement",
    paymentCardHint: "Enregistrer un paiement pour ce membre (hors abonnement ou à titre d’acompte).",
    paymentAmount: "Montant (MAD)",
    paymentMethod: "Mode de paiement",
    paymentSubscriptionLabel: "Rattacher à l’abonnement",
    paymentSubscriptionHint:
      "Le paiement apparaîtra sur la fiche de cet abonnement (facture, solde).",
    paymentSubmit: "Enregistrer le paiement",
    paymentOpen: "Paiement",
    paymentHide: "Masquer",
    paymentSuccess: "Paiement enregistré.",
    paymentError: "Impossible d’enregistrer le paiement.",
    paymentInvalidAmount: "Indiquez un montant valide (supérieur à 0).",
    identitySection: "Identité et contact",
    firstName: "Prénom",
    lastName: "Nom",
  },

  tables: {
    pack: "Pack",
    plan: "Formule",
    start: "Début",
    end: "Fin",
    price: "Prix",
    amount: "Montant",
    method: "Méthode",
    date: "Date",
    invoice: "Facture",
    paymentType: "Type",
  },
})

/** @param {string} [status] */
export function subscriptionStatusLabel(status) {
  const k = String(status || "").toLowerCase()
  return fr.status[k] || status || fr.common.unknown
}

/** @param {string} [status] */
export function accessStatusLabel(status) {
  const k = String(status || "").toLowerCase()
  return fr.status[k] || status || fr.common.unknown
}

/** @param {string} [method] */
export function paymentMethodLabel(method) {
  const k = String(method || "").toLowerCase()
  return fr.paymentMethod[k] || method || fr.common.unknown
}

/** @param {string} [t] */
export function paymentTypeLabel(t) {
  if (t == null || t === "") return "—"
  const k = String(t).toLowerCase()
  if (k === "full") return fr.wizard.stepPayment.paymentTypeFull
  if (k === "partial") return fr.wizard.stepPayment.paymentTypePartial
  return String(t)
}

/** @param {string} [method] */
export function accessMethodLabel(method) {
  const k = String(method || "").toLowerCase()
  const map = { face: "Visage", qr: "QR code", fingerprint: "Empreinte" }
  return map[k] || method || fr.common.unknown
}

/** @param {string} [g] */
export function genderLabel(g) {
  const k = String(g || "").toLowerCase()
  if (k === "male" || k === "m") return fr.gender.male
  if (k === "female" || k === "f") return fr.gender.female
  return g || "—"
}
